<template>
  <Drawer :model-value="modelValue" @update:model-value="$emit('update:modelValue', $event)">
    <div class="modal-header">
      <h2 class="modal-title">Importar OFX</h2>
      <button type="button" class="modal-close" aria-label="Fechar" @click="close">
        <i class="material-icons">close</i>
      </button>
    </div>

    <div class="modal-body ofx-wizard-body">
      <div v-if="step === 1" class="ofx-step">
        <div class="form-group">
          <label for="ofx-account">Conta destino</label>
          <v-select
            id="ofx-account"
            :options="accountOptions"
            :reduce="(o) => o.id"
            v-model="selectedAccountId"
            label="label"
            placeholder="Selecione a conta"
            :clearable="false"
            :disabled="accountOptions.length === 0"
          />
          <p v-if="accountError" class="field-error" role="alert">{{ accountError }}</p>
        </div>
        <div class="form-group">
          <label id="ofx-file-label">Arquivo .ofx</label>
          <input
            id="ofx-file"
            ref="fileInputRef"
            type="file"
            accept=".ofx,.OFX"
            class="ofx-file-input-hidden"
            aria-labelledby="ofx-file-label"
            @change="onFileChange"
          />
          <button
            type="button"
            class="ofx-file-picker"
            :class="{ 'ofx-file-picker--selected': !!fileName, 'ofx-file-picker--error': !!fileError }"
            @click="openFilePicker"
            @dragover.prevent
            @drop.prevent="onFileDrop"
          >
            <span class="ofx-file-picker-icon" aria-hidden="true">
              <i class="material-icons">{{ fileName ? 'description' : 'upload_file' }}</i>
            </span>
            <span class="ofx-file-picker-text">
              <span v-if="fileName" class="ofx-file-picker-name">{{ fileName }}</span>
              <template v-else>
                <span class="ofx-file-picker-title">Selecionar arquivo .ofx</span>
                <span class="ofx-file-picker-hint">Toque para escolher ou arraste o arquivo aqui</span>
              </template>
            </span>
            <span v-if="fileName" class="ofx-file-picker-change">Alterar</span>
          </button>
          <p v-if="fileError" class="field-error" role="alert">{{ fileError }}</p>
        </div>
        <p v-if="parseErrors.length" class="field-error" role="alert">
          {{ parseErrors.join(' ') }}
        </p>
      </div>

      <div v-else class="ofx-step">
        <div class="ofx-summary card">
          <p>
            <strong>{{ toImport.length }}</strong> para importar ·
            <strong>{{ toSkip.length }}</strong> ignoradas
          </p>
          <p v-if="meta.org" class="field-hint">{{ meta.org }} · {{ meta.acctId || '' }}</p>
        </div>

        <div class="ofx-preview-table-wrap">
          <table class="ofx-preview-table">
            <thead>
              <tr>
                <th>Status</th>
                <th>Data</th>
                <th>Descrição</th>
                <th class="num">Valor</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(row, idx) in previewRows" :key="idx" :class="row._skip ? 'ofx-row-skip' : 'ofx-row-import'">
                <td>
                  <span v-if="row._skip" class="ofx-badge ofx-badge-skip">Ignorar</span>
                  <span v-else class="ofx-badge ofx-badge-import">Importar</span>
                </td>
                <td>{{ formatDate(row.date) }}</td>
                <td>{{ row.description }}</td>
                <td class="num" :class="amountClass(row.amount)">{{ formatBrl(row.amount) }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <div class="modal-footer">
      <button type="button" class="btn btn-secondary" :disabled="importLoading" @click="handleBackOrClose">
        {{ step === 2 ? 'Voltar' : 'Cancelar' }}
      </button>
      <button
        v-if="step === 1"
        type="button"
        class="btn btn-primary"
        :disabled="!canPreview"
        @click="goToPreview"
      >
        Pré-visualizar
      </button>
      <button
        v-else
        type="button"
        class="btn btn-primary"
        :disabled="importLoading || toImport.length === 0"
        @click="confirmImport"
      >
        {{ importLoading ? 'Importando...' : `Importar ${toImport.length}` }}
      </button>
    </div>
  </Drawer>
</template>

<script>
import Drawer from '@/components/Drawer.vue';
import vSelect from 'vue-select';
import 'vue-select/dist/vue-select.css';
import { ref, computed, watch } from 'vue';
import { parseOfx } from '@/utils/ofxParser';
import { classifyOfxImport } from '@/utils/bankTransactionDedup';

export default {
  name: 'OfxImportWizard',
  components: { Drawer, vSelect },
  props: {
    modelValue: { type: Boolean, default: false },
    accounts: { type: Array, default: () => [] },
    existingTransactions: { type: Array, default: () => [] },
    importLoading: { type: Boolean, default: false },
  },
  emits: ['update:modelValue', 'import'],
  setup(props, { emit }) {
    const step = ref(1);
    const selectedAccountId = ref(null);
    const fileName = ref('');
    const fileContent = ref('');
    const parseErrors = ref([]);
    const meta = ref({});
    const toImport = ref([]);
    const toSkip = ref([]);
    const accountError = ref('');
    const fileError = ref('');
    const fileInputRef = ref(null);

    const accountOptions = computed(() =>
      props.accounts.map((a) => ({ id: a.id, label: a.name }))
    );

    const selectedAccount = computed(() =>
      props.accounts.find((a) => a.id === selectedAccountId.value) || null
    );

    const canPreview = computed(() => !!selectedAccountId.value && !!fileContent.value);

    const previewRows = computed(() => [
      ...toImport.value.map((r) => ({ ...r, _skip: false })),
      ...toSkip.value.map((r) => ({ ...r, _skip: true })),
    ].sort((a, b) => (Number(a.date) || 0) - (Number(b.date) || 0)));

    function reset() {
      step.value = 1;
      selectedAccountId.value = null;
      fileName.value = '';
      fileContent.value = '';
      parseErrors.value = [];
      meta.value = {};
      toImport.value = [];
      toSkip.value = [];
      accountError.value = '';
      fileError.value = '';
      if (fileInputRef.value) fileInputRef.value.value = '';
    }

    watch(() => props.modelValue, (open) => {
      if (!open) reset();
    });

    function close() {
      emit('update:modelValue', false);
    }

    function handleBackOrClose() {
      if (step.value === 2) {
        step.value = 1;
        return;
      }
      close();
    }

    function openFilePicker() {
      fileInputRef.value?.click();
    }

    function loadFile(file) {
      fileError.value = '';
      parseErrors.value = [];
      if (!file) {
        fileName.value = '';
        fileContent.value = '';
        return;
      }
      const lowerName = file.name.toLowerCase();
      if (!lowerName.endsWith('.ofx')) {
        fileError.value = 'Selecione um arquivo com extensão .ofx.';
        fileName.value = '';
        fileContent.value = '';
        if (fileInputRef.value) fileInputRef.value.value = '';
        return;
      }
      fileName.value = file.name;
      const reader = new FileReader();
      reader.onload = () => {
        fileContent.value = String(reader.result || '');
      };
      reader.onerror = () => {
        fileError.value = 'Não foi possível ler o arquivo.';
      };
      reader.readAsText(file, 'UTF-8');
    }

    function onFileChange(e) {
      loadFile(e.target.files?.[0]);
    }

    function onFileDrop(e) {
      loadFile(e.dataTransfer?.files?.[0]);
    }

    function goToPreview() {
      accountError.value = '';
      fileError.value = '';
      if (!selectedAccountId.value) {
        accountError.value = 'Selecione a conta destino.';
        return;
      }
      if (!fileContent.value) {
        fileError.value = 'Selecione um arquivo .ofx.';
        return;
      }

      const { transactions, meta: parsedMeta, errors } = parseOfx(fileContent.value, {
        accountType: selectedAccount.value?.type,
      });
      parseErrors.value = errors;
      meta.value = parsedMeta;

      if (transactions.length === 0) {
        fileError.value = 'Nenhuma transação encontrada no arquivo.';
        return;
      }

      const accountTx = props.existingTransactions.filter(
        (tx) => tx.accountId === selectedAccountId.value
      );
      const classified = classifyOfxImport(transactions, accountTx);
      toImport.value = classified.toImport;
      toSkip.value = classified.toSkip;
      step.value = 2;
    }

    function confirmImport() {
      emit('import', {
        accountId: selectedAccountId.value,
        fileName: fileName.value,
        meta: meta.value,
        transactions: toImport.value,
      });
    }

    function formatBrl(n) {
      return Number(n).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
    }

    function formatDate(ms) {
      const d = new Date(ms);
      const pad = (x) => (x < 10 ? '0' : '') + x;
      return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()}`;
    }

    function amountClass(amount) {
      if (amount > 0) return 'amount-in';
      if (amount < 0) return 'amount-out';
      return '';
    }

    return {
      step,
      selectedAccountId,
      fileName,
      parseErrors,
      meta,
      toImport,
      toSkip,
      accountError,
      fileError,
      fileInputRef,
      accountOptions,
      canPreview,
      previewRows,
      close,
      handleBackOrClose,
      openFilePicker,
      onFileChange,
      onFileDrop,
      goToPreview,
      confirmImport,
      formatBrl,
      formatDate,
      amountClass,
    };
  },
};
</script>

<style scoped>
.ofx-wizard-body {
  max-height: 60vh;
  overflow-y: auto;
}

.ofx-file-input-hidden {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}

.ofx-file-picker {
  display: flex;
  align-items: center;
  gap: var(--space-md);
  width: 100%;
  padding: var(--space-md);
  border: 1px dashed var(--color-border);
  border-radius: var(--radius-sm);
  background: var(--color-surface);
  color: var(--color-text);
  cursor: pointer;
  text-align: left;
  transition: border-color 0.2s, background 0.2s, box-shadow 0.2s;
}

.ofx-file-picker:hover {
  border-color: var(--color-accent);
  background: var(--color-accent-soft);
}

.ofx-file-picker:focus-visible {
  outline: none;
  border-color: var(--color-border-focus);
  box-shadow: 0 0 0 3px var(--color-accent-soft);
}

.ofx-file-picker--selected {
  border-style: solid;
}

.ofx-file-picker--error {
  border-color: var(--color-error);
}

.ofx-file-picker-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: var(--radius-sm);
  background: var(--color-accent-soft);
  color: var(--color-accent);
  flex-shrink: 0;
}

.ofx-file-picker-icon .material-icons {
  font-size: 22px;
}

.ofx-file-picker-text {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
  flex: 1;
}

.ofx-file-picker-title {
  font-size: 0.95rem;
  font-weight: 600;
  color: var(--color-text);
}

.ofx-file-picker-hint {
  font-size: 0.8rem;
  color: var(--color-text-muted);
}

.ofx-file-picker-name {
  font-size: 0.95rem;
  font-weight: 600;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.ofx-file-picker-change {
  font-size: 0.8rem;
  font-weight: 600;
  color: var(--color-accent);
  flex-shrink: 0;
}

.ofx-summary {
  padding: var(--space-md);
  margin-bottom: var(--space-md);
}

.ofx-preview-table-wrap {
  overflow-x: auto;
}

.ofx-preview-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.875rem;
}

.ofx-preview-table th,
.ofx-preview-table td {
  padding: var(--space-xs) var(--space-sm);
  border-bottom: 1px solid var(--color-border);
  text-align: left;
}

.ofx-preview-table th.num,
.ofx-preview-table td.num {
  text-align: right;
  font-variant-numeric: tabular-nums;
}

.ofx-badge {
  display: inline-block;
  font-size: 0.7rem;
  font-weight: 600;
  padding: 2px 6px;
  border-radius: var(--radius-sm);
}

.ofx-badge-import {
  background: var(--color-accent-soft);
  color: var(--color-accent);
}

.ofx-badge-skip {
  background: var(--color-border);
  color: var(--color-text-secondary);
}

.ofx-row-skip {
  opacity: 0.65;
}

.amount-in {
  color: var(--color-success, #2e7d32);
}

.amount-out {
  color: var(--color-error);
}

.field-error {
  font-size: 0.875rem;
  color: var(--color-error);
  margin-top: var(--space-xs);
}

.field-hint {
  font-size: 0.8rem;
  color: var(--color-text-muted);
  margin: var(--space-xs) 0 0;
}
</style>
